import i18next from 'i18next';
import { APIRequestLabel } from './consts';

interface APIResponseState {
  response: any;
  status: number;
  error: any;
  isLoading: boolean;
}

function updateState(
  getter: Function,
  setter: Function,
  apiRequestLabel: APIRequestLabel,
  value: APIResponseState
) {
  if (
    value?.error?.status === 401 ||
    (value?.error?.status === 500 &&
      (value?.error?.payload?.code === 'ERR_FAILED_REFRESH_GRANT' ||
        value?.error?.payload?.code === 'ERR_EXPIRED_ACCESS_TOKEN'))
  ) {
    setter({
      token: null
    });
    return;
  }

  setter({
    apiCallStatus: {
      ...getter().apiCallStatus,
      [apiRequestLabel]: value
    }
  });
}

export const buildAPIUrl = (url: string): string => {
  const apiUrl = process.env.API_BASE_URL || process.env.NEXT_PUBLIC_API_BASE_URL;
  return `${apiUrl}${url}`;
};

/**
 * @deprecated Use useImprvApi with the react hook instead
 */
async function authenticatedFetch(token: string, url: string, options = {}) {
  const _url = buildAPIUrl(url);
  return fetch(_url, {
    ...options,
    headers: {
      ...(options['headers'] || {}),
      Authorization: `Bearer ${token}`,
      'Accept-Language': i18next.language || navigator.language || 'en'
    }
  });
}

const buildQueryString = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null) {
          searchParams.append(key, item.toString());
        }
      });
    } else if (value !== undefined && value !== null) {
      searchParams.set(key, value.toString());
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

export { buildQueryString };

/**
 * @deprecated Use useImprvApi with the react hook instead
 */
export const callHttpGet = async (
  getter: Function,
  setter: Function,
  apiRequestLabel: APIRequestLabel,
  url: string,
  options = {},
  queryParams = {}
): Promise<APIResponseState> => {
  updateState(getter, setter, apiRequestLabel, {
    response: null,
    status: null,
    error: null,
    isLoading: true
  });

  try {
    // BACKWARD COMPATIBILITY: this is a temporary solution to ensure that we're able to fetch
    // the token globally without the need to pass the token directly here
    const token = await getter().imprvApi.getAPIToken();
    const queryString = buildQueryString(queryParams);
    const res = await authenticatedFetch(token, `${url}${queryString}`, {
      ...options,
      headers: {
        ...(options['headers'] || {})
      }
    });

    let responseState: APIResponseState;
    if (res.status >= 400) {
      responseState = {
        response: null,
        status: res.status,
        error: { payload: await res.json(), status: res.status },
        isLoading: false
      };
    } else {
      responseState = {
        response: await res.json(),
        status: res.status,
        error: null,
        isLoading: false
      };
    }
    updateState(getter, setter, apiRequestLabel, responseState);
    return responseState;
  } catch (error) {
    const errorState: APIResponseState = {
      response: null,
      status: error?.response?.status,
      error: error?.message || error?.errorCode,
      isLoading: false
    };
    updateState(getter, setter, apiRequestLabel, errorState);
    return errorState;
  }
};

/**
 * @deprecated Use useImprvApi with the react hook instead
 */
export const callHttpPost = async (
  getter: Function,
  setter: Function,
  apiRequestLabel: APIRequestLabel,
  url: string,
  body?: any
): Promise<APIResponseState> => {
  updateState(getter, setter, apiRequestLabel, {
    response: null,
    status: null,
    error: null,
    isLoading: true
  });

  try {
    // BACKWARD COMPATIBILITY: this is a temporary solution to ensure that we're able to fetch
    // the token globally without the need to pass the token directly here
    const token = await getter().imprvApi.getAPIToken();
    const res = await authenticatedFetch(token, url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: body ? JSON.stringify(body) : null
    });

    let responseState: APIResponseState;
    if (res.status >= 400) {
      responseState = {
        response: null,
        status: res.status,
        error: { payload: await res.json(), status: res.status },
        isLoading: false
      };
    } else {
      responseState = {
        response: await res.json(),
        status: res.status,
        error: null,
        isLoading: false
      };
    }
    updateState(getter, setter, apiRequestLabel, responseState);
    return responseState;
  } catch (error) {
    const errorState: APIResponseState = {
      response: null,
      status: error?.response?.status,
      error: error?.message || error?.errorCode,
      isLoading: false
    };
    updateState(getter, setter, apiRequestLabel, errorState);
    return errorState;
  }
};

export const callHttpPut = async (
  getter: Function,
  setter: Function,
  apiRequestLabel: APIRequestLabel,
  url: string,
  body: any
): Promise<APIResponseState> => {
  updateState(getter, setter, apiRequestLabel, {
    response: null,
    status: null,
    error: null,
    isLoading: true
  });

  try {
    // BACKWARD COMPATIBILITY: this is a temporary solution to ensure that we're able to fetch
    // the token globally without the need to pass the token directly here
    const token = await getter().imprvApi.getAPIToken();
    const res = await authenticatedFetch(token, url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    let responseState: APIResponseState;
    if (res.status >= 400) {
      responseState = {
        response: null,
        status: res.status,
        error: { payload: await res.json(), status: res.status },
        isLoading: false
      };
    } else {
      responseState = {
        response: await res.json(),
        status: res.status,
        error: null,
        isLoading: false
      };
    }
    updateState(getter, setter, apiRequestLabel, responseState);
    return responseState;
  } catch (error) {
    const errorState: APIResponseState = {
      response: null,
      status: error?.response?.status,
      error: error?.message || error?.errorCode,
      isLoading: false
    };
    updateState(getter, setter, apiRequestLabel, errorState);
    return errorState;
  }
};

export const callHttpPatch = async (
  getter: Function,
  setter: Function,
  apiRequestLabel: APIRequestLabel,
  url: string,
  body: any
): Promise<APIResponseState> => {
  updateState(getter, setter, apiRequestLabel, {
    response: null,
    status: null,
    error: null,
    isLoading: true
  });

  try {
    // BACKWARD COMPATIBILITY: this is a temporary solution to ensure that we're able to fetch
    // the token globally without the need to pass the token directly here
    const token = await getter().imprvApi.getAPIToken();
    const res = await authenticatedFetch(token, url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    let responseState: APIResponseState;
    if (res.status >= 400) {
      responseState = {
        response: null,
        status: res.status,
        error: { payload: await res.json(), status: res.status },
        isLoading: false
      };
    } else {
      responseState = {
        response: await res.json(),
        status: res.status,
        error: null,
        isLoading: false
      };
    }
    updateState(getter, setter, apiRequestLabel, responseState);
    return responseState;
  } catch (error) {
    const errorState: APIResponseState = {
      response: null,
      status: error?.response?.status,
      error: error?.message || error?.errorCode,
      isLoading: false
    };
    updateState(getter, setter, apiRequestLabel, errorState);
    return errorState;
  }
};

export const callHttpDelete = async (
  getter: Function,
  setter: Function,
  apiRequestLabel: APIRequestLabel,
  url: string,
  body?: any
): Promise<APIResponseState> => {
  updateState(getter, setter, apiRequestLabel, {
    response: null,
    status: null,
    error: null,
    isLoading: true
  });

  try {
    // BACKWARD COMPATIBILITY: this is a temporary solution to ensure that we're able to fetch
    // the token globally without the need to pass the token directly here
    const token = await getter().imprvApi.getAPIToken();
    let extra: any = {};
    if (body) {
      extra = {
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      };
    }

    const res = await authenticatedFetch(token, url, {
      method: 'DELETE',
      ...extra
    });

    let responseState: APIResponseState;
    if (res.status >= 400) {
      responseState = {
        response: null,
        status: res.status,
        error: { payload: await res.json(), status: res.status },
        isLoading: false
      };
    } else {
      responseState = {
        response: await res.json(),
        status: res.status,
        error: null,
        isLoading: false
      };
    }
    updateState(getter, setter, apiRequestLabel, responseState);
    return responseState;
  } catch (error) {
    const errorState: APIResponseState = {
      response: null,
      status: error?.response?.status,
      error: error?.message || error?.errorCode,
      isLoading: false
    };
    updateState(getter, setter, apiRequestLabel, errorState);
    return errorState;
  }
};
